import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;
import { 
  Frame,
  Heart, 
  Star, 
  CheckCircle, 
  Clock, 
  Users, 
  Sparkles,
  ChevronRight,
  Upload,
  X,
  Loader2,
  MessageSquare,
  Shield,
  Zap,
  ArrowRight,
  TreeDeciduous,
  Home,
  Gift,
  Image,
  Award,
  Ruler,
  Palette
} from 'lucide-react';
import { toast } from 'sonner';

// Wood types
const WOOD_TYPES = [
  { value: 'noce', label: 'Legno di noce', icon: TreeDeciduous },
  { value: 'ciliegio', label: 'Legno di ciliegio', icon: TreeDeciduous },
  { value: 'rovere', label: 'Legno di rovere', icon: TreeDeciduous },
  { value: 'frassino', label: 'Legno di frassino', icon: TreeDeciduous },
  { value: 'acero', label: 'Legno di acero', icon: TreeDeciduous },
  { value: 'tiglio', label: 'Legno di tiglio', icon: TreeDeciduous },
  { value: 'mogano', label: 'Mogano', icon: TreeDeciduous },
  { value: 'abete', label: 'Abete antico / Recupero', icon: TreeDeciduous },
  { value: 'misto', label: 'Legni misti / Intarsio', icon: Sparkles },
  { value: 'non_so', label: 'Non so, vorrei consiglio', icon: Sparkles },
];

// Frame types
const FRAME_TYPES = [
  { value: 'classica', label: 'Cornice classica', icon: Frame },
  { value: 'moderna', label: 'Cornice moderna / Minimal', icon: Frame },
  { value: 'barocca', label: 'Cornice barocca / Intagliata', icon: Sparkles },
  { value: 'rustica', label: 'Cornice rustica / Country', icon: TreeDeciduous },
  { value: 'shabby', label: 'Cornice shabby chic', icon: Heart },
  { value: 'doppia', label: 'Cornice doppia / Passepartout', icon: Frame },
  { value: 'specchio', label: 'Cornice per specchio', icon: Frame },
  { value: 'multi', label: 'Cornice multi-foto', icon: Image },
  { value: 'teca', label: 'Teca / Shadow box', icon: Frame },
  { value: 'altro', label: 'Altro', icon: Frame },
];

// Size options
const SIZE_OPTIONS = [
  { value: 'piccola', label: 'Piccola (fino a 20x25 cm)', icon: Ruler },
  { value: 'media', label: 'Media (30x40 cm)', icon: Ruler },
  { value: 'grande', label: 'Grande (50x70 cm)', icon: Ruler },
  { value: 'xl', label: 'Extra Large (70x100 cm+)', icon: Ruler },
  { value: 'personalizzata', label: 'Misure personalizzate', icon: Sparkles },
];

// Finish options
const FINISH_OPTIONS = [
  { value: 'naturale', label: 'Finitura naturale (olio/cera)', icon: TreeDeciduous },
  { value: 'laccata', label: 'Laccata (bianca, nera, colorata)', icon: Palette },
  { value: 'dorata', label: 'Dorata / Foglia oro', icon: Star },
  { value: 'argentata', label: 'Argentata / Foglia argento', icon: Star },
  { value: 'decapata', label: 'Decapata / Patinata', icon: Sparkles },
  { value: 'anticata', label: 'Anticata / Invecchiata', icon: TreeDeciduous },
  { value: 'non_so', label: 'Non so, vorrei consiglio', icon: Sparkles },
];

// Content type
const CONTENT_OPTIONS = [
  { value: 'foto', label: 'Fotografia', icon: Image },
  { value: 'dipinto', label: 'Dipinto / Opera d\'arte', icon: Palette },
  { value: 'stampa', label: 'Stampa / Poster', icon: Image },
  { value: 'specchio', label: 'Specchio', icon: Frame },
  { value: 'ricamo', label: 'Ricamo / Tessuto', icon: Heart },
  { value: 'documento', label: 'Documento / Diploma', icon: Award },
  { value: 'oggetto3d', label: 'Oggetto 3D / Cimeli', icon: Frame },
  { value: 'vuota', label: 'Cornice decorativa vuota', icon: Frame },
];

// Purpose/Occasion
const PURPOSE_OPTIONS = [
  { value: 'casa', label: 'Per arredare casa', icon: Home },
  { value: 'regalo', label: 'Come regalo', icon: Gift },
  { value: 'matrimonio', label: 'Regalo matrimonio / Lista nozze', icon: Heart },
  { value: 'commemorativo', label: 'Ricordo / Commemorativo', icon: Heart },
  { value: 'professionale', label: 'Per studio / Ufficio', icon: Award },
  { value: 'collezione', label: 'Per collezione / Galleria', icon: Star },
];

// Budget options
const BUDGET_OPTIONS = [
  { value: '30-60', label: '€30 - €60', description: 'Cornice semplice' },
  { value: '60-100', label: '€60 - €100', description: 'Legno pregiato' },
  { value: '100-180', label: '€100 - €180', description: 'Con lavorazioni' },
  { value: '180-300', label: '€180 - €300', description: 'Intaglio / Doratura' },
  { value: '300-500', label: '€300 - €500', description: 'Pezzo da museo' },
  { value: '500+', label: '€500+', description: 'Opera d\'arte / Grande formato' },
];

// Timeline options
const TIMELINE_OPTIONS = [
  { value: '1week', label: '1 Settimana', description: 'Urgente' },
  { value: '2weeks', label: '2 Settimane', description: 'Prioritario' },
  { value: '3weeks', label: '3 Settimane', description: 'Standard' },
  { value: '1month', label: '1 Mese', description: 'Con calma' },
  { value: 'flexible', label: 'Flessibile', description: 'Nessuna urgenza' },
];

// Example frames
const FRAME_EXAMPLES = [
  {
    idea: "Cornice per foto matrimonio dei nonni, stile antico",
    result: "Cornice in noce intagliata a mano con motivi floreali, finitura anticata e passepartout in velluto",
    image: "https://images.unsplash.com/photo-1582053433976-25c00369fc93?w=400&h=300&fit=crop",
    category: "Commemorativo"
  },
  {
    idea: "Cornice moderna per stampa d'arte contemporanea",
    result: "Cornice minimal in rovere naturale, profilo sottile, vetro antiriflesso museale",
    image: "https://images.unsplash.com/photo-1513519245088-0e12902e35ca?w=400&h=300&fit=crop",
    category: "Moderna"
  },
  {
    idea: "Specchiera per ingresso in stile barocco",
    result: "Cornice intagliata a mano in tiglio, foglia oro zecchino, specchio bisellato 80x120cm",
    image: "https://images.unsplash.com/photo-1618220179428-22790b461013?w=400&h=300&fit=crop",
    category: "Classica"
  }
];

// Testimonials
const TESTIMONIALS = [
  {
    text: "Ho fatto incorniciare la foto del matrimonio dei miei genitori con una cornice intagliata a mano. È diventata il pezzo forte del soggiorno, tutti gli ospiti la ammirano!",
    author: "Valentina G.",
    location: "Venezia"
  },
  {
    text: "Cercavo una cornice per un dipinto di famiglia del '700. L'artigiano ha creato una replica perfetta dello stile dell'epoca. Competenza e passione incredibili.",
    author: "Alessandro D.",
    location: "Firenze"
  },
  {
    text: "Volevo una specchiera grande per il bagno ma non trovavo nulla di bello nei negozi. Quella su misura in legno di recupero è stupenda e unica.",
    author: "Chiara M.",
    location: "Milano"
  }
];

// FAQ items
const FAQ_ITEMS = [
  {
    question: "Quale legno è migliore per una cornice?",
    answer: "Dipende dallo stile: il noce e il ciliegio sono classici ed eleganti, il rovere è robusto e moderno, il tiglio è ideale per intagli elaborati. Per cornici rustiche, il legno di recupero ha un fascino unico."
  },
  {
    question: "Posso avere una cornice dorata artigianale?",
    answer: "Sì, i nostri artigiani applicano la foglia oro vera con tecniche tradizionali. È un processo lungo ma il risultato è incomparabile rispetto alle dorature industriali."
  },
  {
    question: "Come funziona per le misure personalizzate?",
    answer: "Basta indicare le dimensioni esatte di ciò che vuoi incorniciare. L'artigiano calcolerà le dimensioni della cornice considerando eventuali passepartout e margini."
  },
  {
    question: "Quanto tempo ci vuole per una cornice su misura?",
    answer: "Una cornice semplice richiede 1-2 settimane. Per lavorazioni complesse come intagli o doratura a foglia, calcola 3-4 settimane."
  },
  {
    question: "Posso far restaurare una cornice antica?",
    answer: "Assolutamente sì! Molti dei nostri artigiani sono specializzati nel restauro. Possono riparare, pulire, ridorare e restituire splendore alle cornici antiche."
  }
];

export default function CorniciRequestPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    wood_type: '',
    frame_type: '',
    size: '',
    finish: '',
    content_type: '',
    purpose: '',
    budget: '',
    timeline: '',
    attachments: []
  });
  
  const [uploadingFile, setUploadingFile] = useState(false);

  useEffect(() => {
    document.title = 'Cornici Su Misura | Falegnameria Artigianale | Rarifex';
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    if (formData.attachments.length + files.length > 5) {
      toast.error('Puoi caricare massimo 5 immagini');
      return;
    }
    
    setUploadingFile(true);
    
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        toast.error('Solo immagini sono permesse');
        continue;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Immagine troppo grande (max 5MB)');
        continue;
      }
      
      try {
        const fileName = `frame-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const filePath = `requests/${fileName}`;
        
        const { error } = await supabase.storage
          .from('rarifex-uploads')
          .upload(filePath, file);
        
        if (error) throw error;
        
        const { data: { publicUrl } } = supabase.storage
          .from('rarifex-uploads')
          .getPublicUrl(filePath);
        
        setFormData(prev => ({
          ...prev,
          attachments: [...prev.attachments, publicUrl]
        }));
      } catch (error) {
        console.error('Upload error:', error);
        toast.error('Errore nel caricamento');
      }
    }
    
    setUploadingFile(false);
  };

  const removeAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.description.trim()) {
      toast.error('Descrivi la cornice che desideri');
      return;
    }
    
    if (!user) {
      setShowAuthPrompt(true);
      localStorage.setItem('rarifex_pending_frame_request', JSON.stringify(formData));
      return;
    }
    
    setLoading(true);
    
    try {
      const woodLabel = WOOD_TYPES.find(t => t.value === formData.wood_type)?.label || '';
      const frameLabel = FRAME_TYPES.find(t => t.value === formData.frame_type)?.label || '';
      const sizeLabel = SIZE_OPTIONS.find(t => t.value === formData.size)?.label || '';
      const finishLabel = FINISH_OPTIONS.find(t => t.value === formData.finish)?.label || '';
      const contentLabel = CONTENT_OPTIONS.find(t => t.value === formData.content_type)?.label || '';
      const purposeLabel = PURPOSE_OPTIONS.find(p => p.value === formData.purpose)?.label || '';
      const budgetLabel = BUDGET_OPTIONS.find(b => b.value === formData.budget)?.label || '';
      const timelineLabel = TIMELINE_OPTIONS.find(t => t.value === formData.timeline)?.label || '';
      
      let fullDescription = formData.description;
      
      const details = [];
      if (frameLabel) details.push(`🖼️  Tipo: ${frameLabel}`);
      if (woodLabel) details.push(`🌳  Legno: ${woodLabel}`);
      if (sizeLabel) details.push(`📐  Dimensioni: ${sizeLabel}`);
      if (finishLabel) details.push(`✨  Finitura: ${finishLabel}`);
      if (contentLabel) details.push(`🎨  Contenuto: ${contentLabel}`);
      if (purposeLabel) details.push(`🎯  Utilizzo: ${purposeLabel}`);
      if (budgetLabel) details.push(`💰  Budget: ${budgetLabel}`);
      if (timelineLabel) details.push(`⏱️  Tempi: ${timelineLabel}`);
      
      if (details.length > 0) {
        const separator = '━━━━━━━━━━━━━━━━━━━━━━━';
        fullDescription += `\n\n${separator}\n✨ DETTAGLI RICHIESTA\n${separator}\n` + details.join('\n');
      }
      
      const requestData = {
        client_id: user.id,
        title: formData.title || `${frameLabel} in ${woodLabel}`.trim() || 'Cornice su misura',
        description: fullDescription,
        category: 'Cornici',
        price_range: formData.budget || 'flexible',
        timeline: formData.timeline || 'flexible',
        attachments: formData.attachments,
      };
      
      const response = await fetch(`${API_URL}/api/requests/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Errore durante la creazione');
      }
      
      toast.success('Richiesta inviata! Riceverai presto proposte dai falegnami.');
      navigate('/requests');
    } catch (error) {
      console.error('Error submitting request:', error);
      toast.error('Errore nell\'invio. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('frame-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <Helmet>
        <title>Cornici Su Misura | Falegnameria Artigianale Italiana | Rarifex</title>
        <meta name="description" content="Cornici in legno su misura realizzate da maestri falegnami italiani. Noce, ciliegio, rovere con intagli e dorature. Artigianato Made in Italy. Gratuito e senza impegno." />
        <meta name="keywords" content="cornice su misura, cornice legno, cornice artigianale, cornice intagliata, cornice dorata, cornice personalizzata, specchiera, falegnameria" />
        <link rel="canonical" href="https://www.rarifex.com/cornici-su-misura" />
        <meta property="og:title" content="Cornici Su Misura | Falegnameria Artigianale Italiana | Rarifex" />
        <meta property="og:description" content="Cornici in legno su misura realizzate da maestri falegnami italiani. Gratuito e senza impegno." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.rarifex.com/cornici-su-misura" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-stone-50/50 via-white to-amber-50/30">
        
        {/* HERO SECTION */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-stone-100/50 via-transparent to-transparent" />
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                
                {/* Left: Hero Content */}
                <div className="text-center lg:text-left">
                  <div className="inline-flex items-center gap-2 bg-stone-100 text-stone-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
                    <Frame className="w-4 h-4" />
                    Falegnameria Su Misura
                  </div>
                  
                  <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
                    La cornice perfetta{' '}
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-stone-600 to-amber-600">
                      creata per te
                    </span>
                  </h1>
                  
                  <p className="text-xl md:text-2xl text-slate-600 mb-8 leading-relaxed">
                    Maestri falegnami italiani creano la cornice dei tuoi sogni.
                  </p>
                  
                  <p className="text-lg text-slate-500 mb-8">
                    Legni pregiati, intagli, dorature: ogni dettaglio su misura.
                    <br />
                    <strong className="text-slate-700">Gratuito e senza impegno.</strong>
                  </p>
                  
                  {/* Trust badges */}
                  <div className="flex flex-wrap justify-center lg:justify-start gap-4 text-sm text-slate-600">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span>Artigiani verificati</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-5 h-5 text-amber-500" />
                      <span>Risposte in 24-48h</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Shield className="w-5 h-5 text-slate-500" />
                      <span>100% Made in Italy</span>
                    </div>
                  </div>
                </div>
                
                {/* Right: Form Card */}
                <div id="frame-form" className="relative">
                  <Card className="p-6 md:p-8 shadow-2xl border-0 bg-white/80 backdrop-blur-sm">
                    <form onSubmit={handleSubmit} className="space-y-5">
                      
                      {/* Title */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Titolo del tuo progetto
                        </Label>
                        <Input
                          placeholder="Es: Cornice per foto matrimonio"
                          value={formData.title}
                          onChange={(e) => handleInputChange('title', e.target.value)}
                          className="mt-1.5"
                          data-testid="frame-title-input"
                        />
                      </div>
                      
                      {/* Description */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Descrivi la cornice che desideri *
                        </Label>
                        <Textarea
                          placeholder="Racconta la tua idea... Stile, legno, finitura, cosa deve incorniciare. Allega foto di riferimento se ne hai."
                          value={formData.description}
                          onChange={(e) => handleInputChange('description', e.target.value)}
                          className="mt-1.5 min-h-[120px]"
                          required
                          data-testid="frame-description-textarea"
                        />
                      </div>
                      
                      {/* Frame Type & Wood Type */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Stile</Label>
                          <select
                            value={formData.frame_type}
                            onChange={(e) => handleInputChange('frame_type', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="frame-type-select"
                          >
                            <option value="">Seleziona stile...</option>
                            {FRAME_TYPES.map(type => (
                              <option key={type.value} value={type.value}>{type.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Legno</Label>
                          <select
                            value={formData.wood_type}
                            onChange={(e) => handleInputChange('wood_type', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="frame-wood-select"
                          >
                            <option value="">Seleziona legno...</option>
                            {WOOD_TYPES.map(type => (
                              <option key={type.value} value={type.value}>{type.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Size & Finish */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Dimensioni</Label>
                          <select
                            value={formData.size}
                            onChange={(e) => handleInputChange('size', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="frame-size-select"
                          >
                            <option value="">Seleziona...</option>
                            {SIZE_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Finitura</Label>
                          <select
                            value={formData.finish}
                            onChange={(e) => handleInputChange('finish', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="frame-finish-select"
                          >
                            <option value="">Seleziona...</option>
                            {FINISH_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Content & Purpose */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Contenuto</Label>
                          <select
                            value={formData.content_type}
                            onChange={(e) => handleInputChange('content_type', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="frame-content-select"
                          >
                            <option value="">Seleziona...</option>
                            {CONTENT_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Utilizzo</Label>
                          <select
                            value={formData.purpose}
                            onChange={(e) => handleInputChange('purpose', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="frame-purpose-select"
                          >
                            <option value="">Seleziona...</option>
                            {PURPOSE_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Budget & Timeline */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Budget</Label>
                          <select
                            value={formData.budget}
                            onChange={(e) => handleInputChange('budget', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="frame-budget-select"
                          >
                            <option value="">Seleziona...</option>
                            {BUDGET_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Tempi</Label>
                          <select
                            value={formData.timeline}
                            onChange={(e) => handleInputChange('timeline', e.target.value)}
                            className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                            data-testid="frame-timeline-select"
                          >
                            <option value="">Seleziona...</option>
                            {TIMELINE_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* File Upload */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Immagini di riferimento (opzionale)
                        </Label>
                        <div className="mt-1.5">
                          <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-slate-200 rounded-lg cursor-pointer hover:border-stone-400 hover:bg-stone-50/50 transition-colors">
                            <Upload className="w-5 h-5 text-slate-400" />
                            <span className="text-sm text-slate-500">
                              {uploadingFile ? 'Caricamento...' : 'Carica immagini (max 5)'}
                            </span>
                            <input
                              type="file"
                              accept="image/*"
                              multiple
                              onChange={handleFileUpload}
                              className="hidden"
                              disabled={uploadingFile}
                            />
                          </label>
                        </div>
                        
                        {formData.attachments.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-3">
                            {formData.attachments.map((url, index) => (
                              <div key={index} className="relative group">
                                <img
                                  src={url}
                                  alt={`Riferimento ${index + 1}`}
                                  className="w-16 h-16 object-cover rounded-lg"
                                />
                                <button
                                  type="button"
                                  onClick={() => removeAttachment(index)}
                                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      {/* Submit Button */}
                      <Button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-gradient-to-r from-stone-600 to-amber-600 hover:from-stone-700 hover:to-amber-700 text-white py-6 text-lg font-semibold shadow-lg"
                        data-testid="frame-submit-button"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            Invio in corso...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-5 h-5 mr-2" />
                            Richiedi Preventivi Gratuiti
                          </>
                        )}
                      </Button>
                      
                      <p className="text-xs text-center text-slate-500">
                        Riceverai proposte da falegnami verificati entro 24-48 ore
                      </p>
                    </form>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Come Funziona
              </h2>
              <p className="text-lg text-slate-600">
                In 4 semplici passi avrai la tua cornice artigianale
              </p>
            </div>
            
            <div className="grid md:grid-cols-4 gap-8 max-w-5xl mx-auto">
              {[
                { icon: MessageSquare, title: 'Descrivi', desc: 'Raccontaci la cornice che desideri' },
                { icon: Users, title: 'Ricevi', desc: 'Proposte da falegnami esperti' },
                { icon: Star, title: 'Scegli', desc: 'L\'artigiano e il progetto ideale' },
                { icon: Heart, title: 'Ammira', desc: 'La tua cornice unica prende vita' },
              ].map((step, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-stone-100 to-amber-100 flex items-center justify-center">
                    <step.icon className="w-8 h-8 text-stone-600" />
                  </div>
                  <h3 className="font-bold text-slate-900 mb-2">{step.title}</h3>
                  <p className="text-sm text-slate-600">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* EXAMPLES */}
        <section className="py-16 bg-gradient-to-b from-stone-50/30 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Cornici Realizzate
              </h2>
              <p className="text-lg text-slate-600">
                Dalla richiesta alla cornice finita
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {FRAME_EXAMPLES.map((example, index) => (
                <Card key={index} className="overflow-hidden hover:shadow-xl transition-shadow">
                  <img
                    src={example.image}
                    alt={example.result}
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-5">
                    <span className="inline-block px-3 py-1 bg-stone-100 text-stone-800 text-xs font-medium rounded-full mb-3">
                      {example.category}
                    </span>
                    <p className="text-sm text-slate-600 mb-2">
                      <strong>Richiesta:</strong> {example.idea}
                    </p>
                    <p className="text-sm text-slate-800">
                      <strong>Risultato:</strong> {example.result}
                    </p>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* TESTIMONIALS */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Cosa Dicono i Clienti
              </h2>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {TESTIMONIALS.map((testimonial, index) => (
                <Card key={index} className="p-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-slate-600 mb-4 italic">"{testimonial.text}"</p>
                  <div className="text-sm">
                    <p className="font-semibold text-slate-900">{testimonial.author}</p>
                    <p className="text-slate-500">{testimonial.location}</p>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 bg-gradient-to-b from-white to-stone-50/30">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8 text-center">
                Domande Frequenti
              </h2>
              
              <div className="space-y-4">
                {FAQ_ITEMS.map((faq, index) => (
                  <Card 
                    key={index}
                    className="overflow-hidden cursor-pointer"
                    onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  >
                    <div className="p-5 flex justify-between items-center">
                      <h3 className="font-semibold text-slate-900">{faq.question}</h3>
                      <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${expandedFaq === index ? 'rotate-90' : ''}`} />
                    </div>
                    {expandedFaq === index && (
                      <div className="px-5 pb-5 text-slate-600">
                        {faq.answer}
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="py-16 bg-gradient-to-r from-stone-600 to-amber-600">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Pronto a creare la tua cornice?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Richiedi preventivi gratuiti dai migliori falegnami italiani
            </p>
            <Button
              onClick={scrollToForm}
              size="lg"
              className="bg-white text-stone-600 hover:bg-stone-50 font-semibold shadow-xl px-8 py-6 text-lg"
              data-testid="frame-final-cta"
            >
              <Sparkles className="w-5 h-5 mr-2" />
              Inizia Ora - È Gratis
            </Button>
          </div>
        </section>
        
        {/* Auth Prompt Modal */}
        {showAuthPrompt && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-slate-900 mb-4">
                Accedi per continuare
              </h3>
              <p className="text-slate-600 mb-6">
                Per inviare la tua richiesta e ricevere proposte dai falegnami, accedi o crea un account.
              </p>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowAuthPrompt(false)}
                >
                  Annulla
                </Button>
                <Button
                  className="flex-1 bg-stone-600 hover:bg-stone-700"
                  onClick={() => navigate('/login?redirect=/cornici-su-misura')}
                >
                  Accedi
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>
    </>
  );
}
